import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { apiCreate, apiDelete, apiList, apiUpdate } from '../api/apiClient';
import { Button } from '../components/ui/Button';
import { CountBand } from '../components/ui/CountBand';
import { Modal } from '../components/ui/Modal';
import { SearchInput } from '../components/ui/SearchInput';
import { Table } from '../components/ui/Table';

type RuleRow = Record<string, any> & { id: string | number; uuid?: string; property_name: string };
type StatusFilter = 'all' | 'active' | 'inactive';
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function conditionLabel(rule: RuleRow): string {
  const days = Array.isArray(rule.days_of_week) ? rule.days_of_week.map((d: number) => DAY_NAMES[d]).filter(Boolean).join(', ') : 'Weekdays';
  return `${days || 'Daily'} · ${String(rule.start_time || '09:00').slice(0, 5)}–${String(rule.end_time || '17:00').slice(0, 5)}`;
}

export const Rules: React.FC = () => {
  const [rules, setRules] = useState<RuleRow[]>([]);
  const [properties, setProperties] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedRule, setSelectedRule] = useState<RuleRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState('');
  const [draft, setDraft] = useState({ property_id: '', rule_name: '', start_time: '09:00', end_time: '17:00', days_of_week: [1, 2, 3, 4, 5] as number[] });
  const [editDraft, setEditDraft] = useState({ rule_name: '', start_time: '09:00', end_time: '17:00', days_of_week: [] as number[] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const propertyRows = await apiList('properties').catch(() => []);
      setProperties(propertyRows);
      const batches = await Promise.all(propertyRows.map(async (property: any) => {
        const rows = await apiList('access_schedules', { property_id: property.id, include_inactive: true }).catch(() => []);
        return rows.map((rule: any) => ({ ...rule, property_name: property.name || 'Property' }));
      }));
      setRules(batches.flat());
    } finally {
      setLoading(false);
    }
  }, []);

  const openRule = (rule: RuleRow) => {
    setSelectedRule(rule);
    setEditDraft({
      rule_name: String(rule.rule_name || ''),
      start_time: String(rule.start_time || '09:00').slice(0, 5),
      end_time: String(rule.end_time || '17:00').slice(0, 5),
      days_of_week: Array.isArray(rule.days_of_week) ? rule.days_of_week : [1, 2, 3, 4, 5],
    });
    setActionError('');
  };

  const updateSelectedRule = async (updates: Record<string, unknown>) => {
    if (!selectedRule) return;
    setSaving(true);
    setActionError('');
    try {
      await apiUpdate('access_schedules', selectedRule.id, updates);
      await load();
      setSelectedRule(null);
    } catch (error: any) {
      setActionError(error?.message || 'Rule update failed');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rules.filter((rule) => {
      const active = rule.is_active !== false;
      if (statusFilter === 'active' && !active) return false;
      if (statusFilter === 'inactive' && active) return false;
      if (!q) return true;
      return `${rule.rule_name || ''} ${rule.rule_type || ''} ${rule.property_name || ''} ${conditionLabel(rule)}`.toLowerCase().includes(q);
    });
  }, [rules, search, statusFilter]);

  const createRule = async () => {
    if (!draft.property_id || !draft.rule_name.trim()) return;
    setCreating(true);
    try {
      await apiCreate('access_schedules', {
        property_id: draft.property_id,
        rule_name: draft.rule_name.trim(),
        rule_type: 'pin',
        days_of_week: draft.days_of_week,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        start_time: draft.start_time,
        end_time: draft.end_time,
        is_active: true,
      });
      setShowCreate(false);
      setDraft({ property_id: '', rule_name: '', start_time: '09:00', end_time: '17:00', days_of_week: [1, 2, 3, 4, 5] });
      await load();
    } finally {
      setCreating(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" /></div>;

  const columns = [
    { key: 'rule', header: 'Rule', render: (rule: RuleRow) => <strong className="font-semibold text-gray-900">{rule.rule_name || 'Rule'}</strong> },
    { key: 'trigger', header: 'Trigger', render: (rule: RuleRow) => String(rule.rule_type || 'Access').replace(/_/g, ' ') },
    { key: 'conditions', header: 'Conditions', render: (rule: RuleRow) => conditionLabel(rule) },
    { key: 'action', header: 'Action', render: () => 'Allow access' },
    { key: 'properties', header: 'Properties', render: (rule: RuleRow) => rule.property_name },
    { key: 'status', header: 'Status', render: (rule: RuleRow) => <span className={`prototype-status ${rule.is_active === false ? 'inactive' : 'active'}`}>{rule.is_active === false ? 'Inactive' : 'Active'}</span> },
    { key: 'actions', header: '', render: (rule: RuleRow) => <button className="row-open" onClick={() => openRule(rule)}>Open <span aria-hidden="true">→</span></button> },
  ];

  return (
    <div className="shadow-list-page prototype-page">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold text-gray-900">Rules</h1><p className="text-gray-500 mt-1">Automation and event policies</p></div>
      </div>
      <div className="prototype-toolbar">
        <SearchInput value={search} onChange={setSearch} placeholder="Search rules..." />
        <label><small>Status</small><select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}><option value="all">All</option><option value="active">Active</option><option value="inactive">Inactive</option></select></label>
        <button className="prototype-refresh" onClick={load} aria-label="Refresh rules"><RefreshCw /> <span>Refresh</span></button>
        <Button onClick={() => setShowCreate(true)}>Create Rule</Button>
      </div>
      <CountBand value={filtered.length} detail="Rules in the current accessible portfolio" />

      <div className="md:hidden grid grid-cols-1 gap-2 mb-4">
        {filtered.length === 0 ? <div className="prototype-empty">No rules configured.</div> : filtered.map((rule) => (
          <article key={rule.id} className="prototype-mobile-card">
            <header><strong>{rule.rule_name}</strong><span className={`prototype-status ${rule.is_active === false ? 'inactive' : 'active'}`}>{rule.is_active === false ? 'Inactive' : 'Active'}</span></header>
            <p>{rule.property_name}</p><small>{conditionLabel(rule)}</small>
            <div className="mobile-card-actions"><button className="row-open" onClick={() => openRule(rule)}>Open <span aria-hidden="true">→</span></button></div>
          </article>
        ))}
      </div>
      <div className="hidden md:block"><Table<RuleRow> columns={columns} data={filtered} emptyMessage="No rules configured" /></div>

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Create Rule">
        <div className="space-y-4">
          <label className="block text-sm font-medium text-gray-700">Property<select className="mt-1 w-full border border-gray-300 px-3 py-2" value={draft.property_id} onChange={(e) => setDraft((v) => ({ ...v, property_id: e.target.value }))}><option value="">Select a property</option>{properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></label>
          <label className="block text-sm font-medium text-gray-700">Rule name<input className="mt-1 w-full border border-gray-300 px-3 py-2" value={draft.rule_name} onChange={(e) => setDraft((v) => ({ ...v, rule_name: e.target.value }))} /></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm font-medium text-gray-700">Starts<input className="mt-1 w-full border border-gray-300 px-3 py-2" type="time" value={draft.start_time} onChange={(e) => setDraft((v) => ({ ...v, start_time: e.target.value }))} /></label>
            <label className="block text-sm font-medium text-gray-700">Ends<input className="mt-1 w-full border border-gray-300 px-3 py-2" type="time" value={draft.end_time} onChange={(e) => setDraft((v) => ({ ...v, end_time: e.target.value }))} /></label>
          </div>
          <fieldset><legend className="text-sm font-medium text-gray-700">Days</legend><div className="mt-2 flex flex-wrap gap-2">{DAY_NAMES.map((name, day) => <label key={name} className="inline-flex items-center gap-1 border border-gray-300 px-2 py-1 text-sm"><input type="checkbox" checked={draft.days_of_week.includes(day)} onChange={() => setDraft(value => ({ ...value, days_of_week: value.days_of_week.includes(day) ? value.days_of_week.filter(item => item !== day) : [...value.days_of_week, day].sort() }))}/>{name}</label>)}</div></fieldset>
          <p className="text-xs text-gray-500">Review the property, schedule, and days before activation. Times use {Intl.DateTimeFormat().resolvedOptions().timeZone}.</p>
          <div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button><Button disabled={creating || !draft.property_id || !draft.rule_name.trim() || draft.days_of_week.length === 0} onClick={createRule}>{creating ? 'Creating…' : 'Create and activate'}</Button></div>
        </div>
      </Modal>

      <Modal open={Boolean(selectedRule)} onClose={() => setSelectedRule(null)} title={selectedRule?.rule_name || 'Rule details'}>
        {selectedRule && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block font-medium text-gray-700 sm:col-span-2">Rule name<input className="mt-1 w-full border border-gray-300 px-3 py-2" value={editDraft.rule_name} onChange={(e) => setEditDraft((v) => ({ ...v, rule_name: e.target.value }))} /></label>
              <label className="block font-medium text-gray-700">Starts<input className="mt-1 w-full border border-gray-300 px-3 py-2" type="time" value={editDraft.start_time} onChange={(e) => setEditDraft((v) => ({ ...v, start_time: e.target.value }))} /></label>
              <label className="block font-medium text-gray-700">Ends<input className="mt-1 w-full border border-gray-300 px-3 py-2" type="time" value={editDraft.end_time} onChange={(e) => setEditDraft((v) => ({ ...v, end_time: e.target.value }))} /></label>
              <fieldset className="sm:col-span-2"><legend className="font-medium text-gray-700">Days</legend><div className="mt-2 flex flex-wrap gap-2">{DAY_NAMES.map((name, day) => <label key={name} className="inline-flex items-center gap-1 border border-gray-300 px-2 py-1"><input type="checkbox" checked={editDraft.days_of_week.includes(day)} onChange={() => setEditDraft(value => ({ ...value, days_of_week: value.days_of_week.includes(day) ? value.days_of_week.filter(item => item !== day) : [...value.days_of_week, day].sort() }))}/>{name}</label>)}</div></fieldset>
              <div><span className="text-gray-500">Property</span><p className="mt-1 font-medium text-gray-900">{selectedRule.property_name}</p></div>
              <div><span className="text-gray-500">Status</span><p className="mt-1"><span className={`prototype-status ${selectedRule.is_active === false ? 'inactive' : 'active'}`}>{selectedRule.is_active === false ? 'Inactive' : 'Active'}</span></p></div>
            </div>
            {actionError && <p className="text-sm text-red-600" role="alert">{actionError}</p>}
            <div className="flex flex-wrap justify-end gap-2 border-t border-gray-200 pt-4">
              <Button variant="secondary" disabled={saving} onClick={() => updateSelectedRule({ is_active: selectedRule.is_active === false })}>{selectedRule.is_active === false ? 'Reactivate' : 'Deactivate'}</Button>
              <Button variant="secondary" disabled={saving} onClick={async () => {
                if (!window.confirm(`Deactivate rule “${selectedRule.rule_name}”?`)) return;
                setSaving(true);
                setActionError('');
                try { await apiDelete('access_schedules', selectedRule.id); await load(); setSelectedRule(null); }
                catch (error: any) { setActionError(error?.message || 'Rule deactivation failed'); }
                finally { setSaving(false); }
              }}>Delete</Button>
              <Button disabled={saving || !editDraft.rule_name.trim() || editDraft.days_of_week.length === 0} onClick={() => updateSelectedRule({ ...editDraft, rule_name: editDraft.rule_name.trim() })}>{saving ? 'Saving…' : 'Save changes'}</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
